import { useState, useEffect, useMemo } from "react";
import { formatDMY } from "../../../../utils/dateFormat";
import BaseModal from "../../../../components/BaseComponents/BaseModal/BaseModal";
import styles from "./ManualScheduleChangeModal.module.css";
import { useImpactPreview, useSupplierSchedule } from "../../../../api/products/useScheduleUpdaterApi";
import { AlertTriangle, ArrowRight, CheckCircle, Clock, Plane, XCircle } from "lucide-react";

// §25 — how each impact result is presented. Ordered worst-first in the panel so
// a conflict is never buried under a list of routine updates.
const IMPACT_META = {
  CRITICAL_CONFLICT: { label: "Conflict", tone: "critical", rank: 0 },
  SUPPLIER_UPDATE_REQUIRED: { label: "Supplier must be told", tone: "warn", rank: 1 },
  MANUAL_REVIEW_REQUIRED: { label: "Needs review", tone: "warn", rank: 2 },
  AUTOMATIC_UPDATE_AVAILABLE: { label: "Updates automatically", tone: "ok", rank: 3 },
  NO_IMPACT: { label: "No impact", tone: "ok", rank: 4 },
};

/* ══════════════════════════════════════════════════════════════════════════
   Manual Schedule Change (docs/flight-schedule-updater.md §37)

   A manual schedule change follows exactly the same downstream workflow as an
   API-derived one (§38), so this modal collects everything that workflow needs:
   the new schedule, how it reached us, its impact, and a MANDATORY reason.

   Two things the spec is firm about and this UI enforces:
     §37  A reason is mandatory — Apply stays disabled without one.
     §23  The operator must never have to diff two flight cards by eye; changed
          fields are highlighted and the time delta is shown.
   ══════════════════════════════════════════════════════════════════════════ */

// §37 — the reasons a manual change legitimately arrives by.
const REASONS = [
  "Airline email",
  "Supplier email",
  "Airline telephone confirmation",
  "Supplier telephone confirmation",
  "Airport information",
  "Manual operational correction",
];

// bookingScheduleChanges.detectedVia ENUM
const DETECTED_VIA = ["Manual", "Email", "GDS", "API"];

// bookingScheduleChanges.impact ENUM (§59 severity, as stored)
const IMPACTS = [
  { value: "Minor", label: "Minor — small timing shift" },
  { value: "Major", label: "Major — several hours, date or flight number" },
  { value: "Cancelled", label: "Cancelled — flight no longer operating" },
];

const MULTIPLE = "—"; // em dash, shown where selected segments disagree

/** One value if every selected segment agrees, otherwise null. */
const sharedValue = (segments, key) => {
  const values = new Set(segments.map((s) => s[key] || ""));
  return values.size === 1 ? [...values][0] : null;
};

const toMinutes = (date, time) => {
  const d = String(date || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!d) return null;
  const t = String(time || "").match(/^(\d{1,2}):(\d{2})/);
  return Math.round(
    Date.UTC(+d[1], +d[2] - 1, +d[3], t ? +t[1] : 0, t ? +t[2] : 0) / 60000
  );
};

/** "+1h50" / "-25m" — the §23 difference, not just the new value. */
const formatDelta = (fromDate, fromTime, toDate, toTime) => {
  const a = toMinutes(fromDate, fromTime);
  const b = toMinutes(toDate, toTime);
  if (a === null || b === null || a === b) return null;
  const diff = b - a;
  const sign = diff > 0 ? "+" : "−";
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h ? `${h}h` : ""}${h && m ? String(m).padStart(2, "0") : h ? "" : `${m}m`}`;
};

const ManualScheduleChangeModal = ({ open, onClose, segments = [], onApply, applying = false }) => {
  const [form, setForm] = useState({
    departureDate: "",
    departureTime: "",
    arrivalDate: "",
    arrivalTime: "",
    flightNumber: "",
    departureAirport: "",
    arrivalAirport: "",
    departureTerminal: "",
    arrivalTerminal: "",
    marketingAirline: "",
    operatingAirline: "",
  });
  const [detectedVia, setDetectedVia] = useState("Manual");
  const [impact, setImpact] = useState("Minor");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [allowInvalidConnection, setAllowInvalidConnection] = useState(false);
  // §28 — modifying a linked transfer is a decision, not a side effect.
  const [syncTransfers, setSyncTransfers] = useState(true);
  // §43 — reissue affected vouchers so they carry the new schedule.
  const [regenerateDocuments, setRegenerateDocuments] = useState(true);
  const [results, setResults] = useState(null);
  const { data: impactPreview, loading: impactLoading, execute: previewImpact } = useImpactPreview();

  // ── Airtuerk is the source for API flights ──────────────────────────────
  // On an API flight the supplier has already stated the new schedule in its
  // file, so the NEW column takes it from there rather than copying CURRENT and
  // making the operator retype it (which is how a typo becomes a schedule that
  // disagrees with the supplier). The fields stay locked to the supplier's
  // figures unless the operator deliberately overrides them.
  const { data: supplier, loading: supplierLoading, execute: loadSupplier, reset: resetSupplier } =
    useSupplierSchedule();
  const [overrideSupplier, setOverrideSupplier] = useState(false);

  const isApiFlight = segments.length > 0 && segments.every((s) => s.source === "API");

  /** One supplier value if every selected segment agrees, otherwise null. */
  const supplierShared = useMemo(() => {
    const map = supplier?.segments || {};
    const picked = segments.map((s) => map[s.flightSegmentId]).filter(Boolean);
    if (!picked.length || picked.length !== segments.length) return null;
    const one = (key) => {
      const values = new Set(picked.map((p) => p[key] || ""));
      return values.size === 1 ? [...values][0] : "";
    };
    return {
      departureDate: one("departureDate"),
      departureTime: one("departureTime"),
      arrivalDate: one("arrivalDate"),
      arrivalTime: one("arrivalTime"),
      flightNumber: one("flightNumber"),
      receivedAt: one("supplierReceivedAt"),
      count: picked.length,
    };
  }, [supplier, segments]);

  // The supplier answered for every selected segment, so its schedule drives the
  // form. A partial answer does not: filling some rows from Airtuerk and leaving
  // others as the booking's current values would read as one coherent schedule
  // when it is two.
  const supplierDrives = isApiFlight && Boolean(supplierShared) && !overrideSupplier;

  // Prefill only where every selected segment agrees — never invent a value that
  // would then be written to segments it was not true for.
  useEffect(() => {
    if (!open) return;
    setForm({
      departureDate: sharedValue(segments, "departureDate") || "",
      departureTime: sharedValue(segments, "departureTime") || "",
      arrivalDate: sharedValue(segments, "arrivalDate") || "",
      arrivalTime: sharedValue(segments, "arrivalTime") || "",
      flightNumber: "",
      departureAirport: "",
      arrivalAirport: "",
      departureTerminal: "",
      arrivalTerminal: "",
      marketingAirline: "",
      operatingAirline: "",
    });
    setOverrideSupplier(false);
    resetSupplier();
    // Ask Airtuerk what it says for these segments. Manual flights have no
    // supplier file by definition, so they are not asked.
    if (segments.some((s) => s.source === "API")) {
      loadSupplier(segments.map((s) => s.productId));
    }
    setDetectedVia("Manual");
    setImpact("Minor");
    setReason("");
    setNotes("");
    setAllowInvalidConnection(false);
    setSyncTransfers(true);
    setRegenerateDocuments(true);
    setResults(null);
  }, [open, segments, loadSupplier, resetSupplier]);

  const current = useMemo(
    () => ({
      departureDate: sharedValue(segments, "departureDate"),
      departureTime: sharedValue(segments, "departureTime"),
      arrivalDate: sharedValue(segments, "arrivalDate"),
      arrivalTime: sharedValue(segments, "arrivalTime"),
      flightNumber: sharedValue(segments, "flightNumber"),
      departureAirport: sharedValue(segments, "departureAirport"),
      arrivalAirport: sharedValue(segments, "arrivalAirport"),
      departureTerminal: sharedValue(segments, "departureTerminal"),
      arrivalTerminal: sharedValue(segments, "arrivalTerminal"),
      marketingAirline: sharedValue(segments, "airlineCode"),
      operatingAirline: sharedValue(segments, "operatingAirlineCode"),
    }),
    [segments]
  );

  // Airtuerk's schedule is asserted into the NEW column whenever it is what
  // drives the form — on arrival, and again if anything typed has diverged from
  // it. That is what "always from Airtuerk" means: the supplier's figures are
  // not merely a starting suggestion that a stray keystroke can quietly replace.
  // Overriding is possible, but only by ticking the override, never by accident.
  // Declared after `current` on purpose: reading it in the dependency array
  // above that binding is a temporal dead zone error, not a hoisting freebie.
  useEffect(() => {
    if (!supplierDrives || !supplierShared) return;
    setForm((prev) => {
      const next = {
        ...prev,
        departureDate: supplierShared.departureDate || prev.departureDate,
        departureTime: supplierShared.departureTime || prev.departureTime,
        arrivalDate: supplierShared.arrivalDate || prev.arrivalDate,
        arrivalTime: supplierShared.arrivalTime || prev.arrivalTime,
        // Flight identity is "leave blank to keep", so it is only filled when
        // the supplier is actually moving the flight number.
        flightNumber:
          supplierShared.flightNumber && supplierShared.flightNumber !== current.flightNumber
            ? supplierShared.flightNumber
            : prev.flightNumber,
      };
      return Object.keys(next).every((k) => next[k] === prev[k]) ? prev : next;
    });
    // The change did come from the supplier's file, so the record says so rather
    // than calling it a manual edit.
    setDetectedVia("API");
  }, [supplierDrives, supplierShared, current.flightNumber]);

  // §39 — segments already corrected by hand. Overwriting one discards a value a
  // human verified, so it is called out before Apply rather than after.
  const overridden = segments.filter((s) => s.manualOverride);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Only fields the operator actually filled in are sent — an empty field means
  // "leave as it is", not "blank it out".
  const changedFields = useMemo(() => {
    const out = {};
    Object.entries(form).forEach(([k, v]) => {
      const value = String(v || "").trim();
      if (!value) return;
      if (current[k] !== null && current[k] === value) return; // identical — not a change
      out[k] = value;
    });
    return out;
  }, [form, current]);

  const hasChange = Object.keys(changedFields).length > 0;
  const canApply = hasChange && Boolean(reason) && !applying;

  // §13 — impact is previewed while the operator types, so it is on screen
  // before they confirm rather than reported afterwards. Debounced so a
  // half-typed time does not fire a request per keystroke.
  const changedKey = JSON.stringify(changedFields);
  useEffect(() => {
    if (!open || !hasChange) return undefined;
    const timer = setTimeout(() => {
      previewImpact(segments, JSON.parse(changedKey));
    }, 450);
    return () => clearTimeout(timer);
  }, [open, hasChange, changedKey, segments, previewImpact]);

  // Everything across the selected segments that needs a human, worst first.
  const attention = (impactPreview?.results || [])
    .flatMap((r) =>
      (r.impact?.requiresAttention || []).map((a) => ({ ...a, bookingNumber: r.bookingNumber }))
    )
    .sort((a, b) => (IMPACT_META[a.impact]?.rank ?? 9) - (IMPACT_META[b.impact]?.rank ?? 9));

  const brokenConnections = (impactPreview?.results || []).filter(
    (r) => r.connection?.status === "CONNECTION_INVALID"
  );
  const transfersAffected = attention.filter((a) => a.productType === "Transfer");

  const departureDelta = formatDelta(
    current.departureDate,
    current.departureTime,
    form.departureDate || current.departureDate,
    form.departureTime || current.departureTime
  );
  const arrivalDelta = formatDelta(
    current.arrivalDate,
    current.arrivalTime,
    form.arrivalDate || current.arrivalDate,
    form.arrivalTime || current.arrivalTime
  );

  const affectedBookings = new Set(segments.map((s) => s.bookingId)).size;
  const affectedTravelers = segments.reduce((n, s) => n + (s.travelers || 0), 0);
  const blockedByConnection = (results?.results || []).some((r) => r.status === "CRITICAL");

  const handleApply = async () => {
    const outcome = await onApply(changedFields, {
      reason,
      detectedVia,
      impact,
      notes: notes.trim() || undefined,
      allowInvalidConnection,
      syncTransfers,
      regenerateDocuments,
    });
    // §70 — the per-booking breakdown stays on screen; a single failure must not
    // be swallowed by an overall "applied" message.
    if (outcome?.data) setResults(outcome.data);
    else if (outcome?.success) onClose();
  };

  const renderCurrent = (key) => (current[key] === null ? MULTIPLE : current[key] || "—");

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Manual Schedule Change"
      width="760px"
      footer={
        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            {results ? "Close" : "Cancel"}
          </button>
          {!results && (
            <button className={styles.applyBtn} onClick={handleApply} disabled={!canApply}>
              {applying
                ? "Applying…"
                : `Apply to ${segments.length} segment${segments.length === 1 ? "" : "s"}`}
            </button>
          )}
          {results && blockedByConnection && (
            <button className={styles.applyBtn} onClick={handleApply} disabled={!allowInvalidConnection || applying}>
              {applying ? "Applying…" : "Apply anyway"}
            </button>
          )}
        </div>
      }
    >
      <div className={styles.body}>
        {/* ── Scope (§14) ───────────────────────────────────────────────── */}
        <div className={styles.scopeBar}>
          <Plane size={16} />
          <span>
            <strong>{segments.length}</strong> flight segment{segments.length === 1 ? "" : "s"} ·{" "}
            <strong>{affectedBookings}</strong> booking{affectedBookings === 1 ? "" : "s"} ·{" "}
            <strong>{affectedTravelers}</strong> traveller{affectedTravelers === 1 ? "" : "s"}
          </span>
          <span className={styles.scopeNote}>Each booking is processed individually.</span>
        </div>

        {!results && (
          <>
            {/* ── Comparison (§23) ───────────────────────────────────────── */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Schedule</h4>

              {/* Where the NEW column came from. An API flight is never left to
                  memory: either Airtuerk stated the schedule and it is used, or
                  it did not and that is said outright. */}
              {isApiFlight && (
                <div
                  className={
                    supplierDrives
                      ? styles.supplierBanner
                      : `${styles.supplierBanner} ${styles.supplierBannerWarn}`
                  }
                >
                  {supplierLoading ? (
                    <span>Reading the schedule Airtuerk has on file…</span>
                  ) : supplierDrives ? (
                    <>
                      <span>
                        <strong>New schedule is Airtuerk's.</strong> Taken from the supplier file
                        {supplierShared?.receivedAt
                          ? ` received ${formatDMY(String(supplierShared.receivedAt).slice(0, 10))}`
                          : ""}
                        , not typed here.
                      </span>
                      <label className={styles.supplierOverride}>
                        <input
                          type="checkbox"
                          checked={overrideSupplier}
                          onChange={(e) => setOverrideSupplier(e.target.checked)}
                        />
                        Enter a different schedule by hand
                      </label>
                    </>
                  ) : overrideSupplier ? (
                    <>
                      <span>
                        <strong>Overriding Airtuerk.</strong> These values are typed by hand and may
                        disagree with the supplier file.
                      </span>
                      <label className={styles.supplierOverride}>
                        <input
                          type="checkbox"
                          checked={overrideSupplier}
                          onChange={(e) => setOverrideSupplier(e.target.checked)}
                        />
                        Enter a different schedule by hand
                      </label>
                    </>
                  ) : (
                    <span>
                      {supplier?.notFound?.[0]?.reason
                        || supplier?.message
                        || "Airtuerk has not stated a schedule for every selected segment, so the new schedule must be entered by hand."}
                    </span>
                  )}
                </div>
              )}

              <div className={styles.compareGrid}>
                <div className={styles.compareHead} />
                <div className={styles.compareHead}>Current</div>
                <div className={styles.compareHead}>New</div>
                <div className={styles.compareHead}>Difference</div>

                <div className={styles.rowLabel}>Departure date</div>
                <div className={styles.currentCell}>{renderCurrent("departureDate")}</div>
                <div>
                  <input
                    type="date"
                    className={changedFields.departureDate ? styles.inputChanged : styles.input}
                    value={form.departureDate}
                    onChange={(e) => set("departureDate", e.target.value)}
                    disabled={supplierDrives}
                  />
                </div>
                <div className={styles.deltaCell} />

                <div className={styles.rowLabel}>Departure time</div>
                <div className={styles.currentCell}>{renderCurrent("departureTime")}</div>
                <div>
                  <input
                    type="time"
                    className={changedFields.departureTime ? styles.inputChanged : styles.input}
                    value={form.departureTime}
                    onChange={(e) => set("departureTime", e.target.value)}
                    disabled={supplierDrives}
                  />
                </div>
                <div className={styles.deltaCell}>
                  {departureDelta && <span className={styles.delta}>{departureDelta}</span>}
                </div>

                <div className={styles.rowLabel}>Arrival date</div>
                <div className={styles.currentCell}>{renderCurrent("arrivalDate")}</div>
                <div>
                  <input
                    type="date"
                    className={changedFields.arrivalDate ? styles.inputChanged : styles.input}
                    value={form.arrivalDate}
                    onChange={(e) => set("arrivalDate", e.target.value)}
                    disabled={supplierDrives}
                  />
                </div>
                <div className={styles.deltaCell} />

                <div className={styles.rowLabel}>Arrival time</div>
                <div className={styles.currentCell}>{renderCurrent("arrivalTime")}</div>
                <div>
                  <input
                    type="time"
                    className={changedFields.arrivalTime ? styles.inputChanged : styles.input}
                    value={form.arrivalTime}
                    onChange={(e) => set("arrivalTime", e.target.value)}
                    disabled={supplierDrives}
                  />
                </div>
                <div className={styles.deltaCell}>
                  {arrivalDelta && <span className={styles.delta}>{arrivalDelta}</span>}
                </div>
              </div>
            </div>

            {/* ── Flight identity (§37) ──────────────────────────────────── */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                Flight identity <span className={styles.optional}>optional — leave blank to keep</span>
              </h4>
              <div className={styles.identityGrid}>
                <label>
                  <span>Flight number</span>
                  <input
                    type="text"
                    placeholder={renderCurrent("flightNumber")}
                    className={changedFields.flightNumber ? styles.inputChanged : styles.input}
                    value={form.flightNumber}
                    onChange={(e) => set("flightNumber", e.target.value)}
                  />
                </label>
                <label>
                  <span>Departure airport</span>
                  <input
                    type="text"
                    maxLength={3}
                    placeholder={renderCurrent("departureAirport")}
                    className={changedFields.departureAirport ? styles.inputChanged : styles.input}
                    value={form.departureAirport}
                    onChange={(e) => set("departureAirport", e.target.value.toUpperCase())}
                  />
                </label>
                <label>
                  <span>Arrival airport</span>
                  <input
                    type="text"
                    maxLength={3}
                    placeholder={renderCurrent("arrivalAirport")}
                    className={changedFields.arrivalAirport ? styles.inputChanged : styles.input}
                    value={form.arrivalAirport}
                    onChange={(e) => set("arrivalAirport", e.target.value.toUpperCase())}
                  />
                </label>
                <label>
                  <span>Departure terminal</span>
                  <input
                    type="text"
                    maxLength={8}
                    placeholder={renderCurrent("departureTerminal")}
                    className={changedFields.departureTerminal ? styles.inputChanged : styles.input}
                    value={form.departureTerminal}
                    onChange={(e) => set("departureTerminal", e.target.value)}
                  />
                </label>
                <label>
                  <span>Arrival terminal</span>
                  <input
                    type="text"
                    maxLength={8}
                    placeholder={renderCurrent("arrivalTerminal")}
                    className={changedFields.arrivalTerminal ? styles.inputChanged : styles.input}
                    value={form.arrivalTerminal}
                    onChange={(e) => set("arrivalTerminal", e.target.value)}
                  />
                </label>
                <label>
                  <span>Marketing airline</span>
                  <input
                    type="text"
                    maxLength={3}
                    placeholder={renderCurrent("marketingAirline")}
                    className={changedFields.marketingAirline ? styles.inputChanged : styles.input}
                    value={form.marketingAirline}
                    onChange={(e) => set("marketingAirline", e.target.value.toUpperCase())}
                  />
                </label>
                <label>
                  <span>Operating airline</span>
                  <input
                    type="text"
                    maxLength={3}
                    placeholder={renderCurrent("operatingAirline") === "—" ? "same as marketing" : renderCurrent("operatingAirline")}
                    className={changedFields.operatingAirline ? styles.inputChanged : styles.input}
                    value={form.operatingAirline}
                    onChange={(e) => set("operatingAirline", e.target.value.toUpperCase())}
                  />
                </label>
              </div>
            </div>

            {/* §39 — overwriting a hand-verified correction, called out first. */}
            {overridden.length > 0 && (
              <div className={styles.overrideBox}>
                <AlertTriangle size={16} />
                <div>
                  <strong>
                    {overridden.length} of these segments {overridden.length === 1 ? "was" : "were"} corrected
                    manually.
                  </strong>{" "}
                  Applying replaces a value someone verified with the airline
                  {overridden[0]?.manualOverrideReason && ` (“${overridden[0].manualOverrideReason}”)`}.
                </div>
              </div>
            )}

            {/* ── Change details (§37, §51) ──────────────────────────────── */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Change details</h4>
              <div className={styles.detailsGrid}>
                <label>
                  <span>Received via</span>
                  <select value={detectedVia} onChange={(e) => setDetectedVia(e.target.value)}>
                    {DETECTED_VIA.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Impact</span>
                  <select value={impact} onChange={(e) => setImpact(e.target.value)}>
                    {IMPACTS.map((i) => (
                      <option key={i.value} value={i.value}>{i.label}</option>
                    ))}
                  </select>
                </label>
                <label className={styles.fullWidth}>
                  <span>
                    Reason <em className={styles.required}>required</em>
                  </span>
                  <select value={reason} onChange={(e) => setReason(e.target.value)}>
                    <option value="">Select a reason…</option>
                    {REASONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </label>
                <label className={styles.fullWidth}>
                  <span>Note / reference <span className={styles.optional}>optional</span></span>
                  <textarea
                    rows={2}
                    value={notes}
                    placeholder="Airline reference, who confirmed it, anything the audit trail should carry…"
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </label>
              </div>
            </div>

            {/* ── Impact on the rest of the booking (§13, §25) ───────────── */}
            {hasChange && (
              <div className={styles.section}>
                <h4 className={styles.sectionTitle}>
                  Impact
                  {impactLoading && <span className={styles.optional}>checking…</span>}
                </h4>

                {brokenConnections.length > 0 && (
                  <div className={styles.criticalBox}>
                    <AlertTriangle size={16} />
                    <div>
                      <strong>
                        This breaks a connection on {brokenConnections.length} booking
                        {brokenConnections.length === 1 ? "" : "s"}.
                      </strong>{" "}
                      {brokenConnections[0].connection.connections
                        ?.filter((c) => c.status === "CONNECTION_INVALID")
                        .map((c) => `${c.inboundFlight} arrives ${c.arrives} but ${c.outboundFlight} departs ${c.departs}`)
                        .join("; ")}
                    </div>
                  </div>
                )}

                {attention.length === 0 && !impactLoading && (
                  <div className={styles.impactClear}>
                    <CheckCircle size={15} />
                    Nothing else in {segments.length === 1 ? "this booking" : "these bookings"} is
                    affected — no transfer, hotel or car rental needs attention.
                  </div>
                )}

                {attention.length > 0 && (
                  <ul className={styles.impactList}>
                    {attention.map((a, i) => {
                      const meta = IMPACT_META[a.impact] || IMPACT_META.NO_IMPACT;
                      return (
                        <li key={`${a.productId}-${i}`} className={styles.impactRow}>
                          <span className={styles[`impactTag_${meta.tone}`]}>{meta.label}</span>
                          <div className={styles.impactBody}>
                            <strong>{a.label}</strong>
                            <span>{a.reason}</span>
                            {a.bookingNumber && (
                              <span className={styles.impactBooking}>{a.bookingNumber}</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* §28 — SUNSKY staff confirm a transfer update; it is not a
                    silent side effect of moving the flight. */}
                {transfersAffected.length > 0 && (
                  <label className={styles.transferConfirm}>
                    <input
                      type="checkbox"
                      checked={syncTransfers}
                      onChange={(e) => setSyncTransfers(e.target.checked)}
                    />
                    <span>
                      Shift the linked transfer{transfersAffected.length === 1 ? "" : "s"} by the same
                      amount.
                      <em>
                        {" "}
                        Unticked, the flight moves and the transfer is left alone for you to handle.
                      </em>
                    </span>
                  </label>
                )}
              </div>
            )}

            {/* §43 — reissuing the paperwork is part of the change, but the
                operator sees it happening rather than discovering it after. */}
            <label className={styles.transferConfirm}>
              <input
                type="checkbox"
                checked={regenerateDocuments}
                onChange={(e) => setRegenerateDocuments(e.target.checked)}
              />
              <span>
                Reissue the affected vouchers with the new schedule.
                <em> Unticked, the customer keeps paperwork showing the old times.</em>
              </span>
            </label>

            {/* What happens next — stated up front, not discovered afterwards. */}
            <div className={styles.noticeBox}>
              <Clock size={16} />
              <div>
                Applying updates the flight segment, writes a schedule-change record and an audit
                entry for every booking, and reissues the vouchers if ticked above.
                <strong> The customer is not emailed</strong> — that is not built yet, so each
                booking stays in the action queue until you have told them.
              </div>
            </div>

            {!hasChange && (
              <div className={styles.hintBox}>
                Enter at least one changed value above to enable Apply.
              </div>
            )}
          </>
        )}

        {/* ── Per-booking results (§70) ──────────────────────────────────── */}
        {results && (
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Result</h4>
            <div className={styles.resultSummary}>
              <span className={styles.pillOk}>{results.summary?.applied ?? 0} updated</span>
              {Boolean(results.summary?.noChange) && (
                <span className={styles.pillNeutral}>{results.summary.noChange} already up to date</span>
              )}
              {Boolean(results.summary?.critical) && (
                <span className={styles.pillCritical}>{results.summary.critical} invalid connection</span>
              )}
              {Boolean(results.summary?.failed) && (
                <span className={styles.pillFail}>{results.summary.failed} failed</span>
              )}
            </div>

            <ul className={styles.resultList}>
              {(results.results || []).map((r, i) => (
                <li key={`${r.productId}-${i}`} className={styles.resultRow}>
                  <span className={styles.resultIcon}>
                    {r.status === "PARTIALLY_UPDATED" && <CheckCircle size={15} className={styles.iconOk} />}
                    {r.status === "NO_CHANGE" && <Clock size={15} className={styles.iconNeutral} />}
                    {r.status === "CRITICAL" && <AlertTriangle size={15} className={styles.iconCritical} />}
                    {r.status === "FAILED" && <XCircle size={15} className={styles.iconFail} />}
                  </span>
                  <span className={styles.resultBooking}>{r.bookingNumber || `Product ${r.productId}`}</span>
                  <span className={styles.resultMessage}>{r.message}</span>
                </li>
              ))}
            </ul>

            {blockedByConnection && (
              <div className={styles.criticalBox}>
                <AlertTriangle size={16} />
                <div>
                  <strong>Nothing was changed on those bookings.</strong> The new schedule makes a
                  connection impossible. Re-time the connecting leg first, or confirm below to record
                  it anyway.
                  <label className={styles.overrideCheck}>
                    <input
                      type="checkbox"
                      checked={allowInvalidConnection}
                      onChange={(e) => setAllowInvalidConnection(e.target.checked)}
                    />
                    Apply despite the invalid connection
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Selected segments, so the operator can see exactly what is in scope. */}
        {!results && segments.length > 0 && (
          <details className={styles.segmentList}>
            <summary>Selected segments ({segments.length})</summary>
            <ul>
              {segments.map((s) => (
                <li key={s.id}>
                  <strong>{s.bookingNumber}</strong>
                  <span>{s.flightNumber}</span>
                  <span>{s.route}</span>
                  <span>
                    {formatDMY(s.departureDate)} {s.departureTime} <ArrowRight size={11} /> {s.arrivalTime}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </BaseModal>
  );
};

export default ManualScheduleChangeModal;
